import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckSquare, DollarSign, Smile, Users, MapPin, Video, Star, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockData } from '../mock';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Home = () => {
  const navigate = useNavigate();

  const ReviewText = ({ comment, isLong }) => {
    const [expanded, setExpanded] = React.useState(false);
    return (
      <div className="mb-6">
        <p className={`text-gray-700 text-sm leading-relaxed ${!expanded && isLong ? '' : ''}`}
          style={!expanded && isLong ? {display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden'} : {}}>
          {comment}
        </p>
        {isLong && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="text-blue-600 text-xs font-medium mt-1 hover:underline"
            data-testid="read-more-btn"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
    );
  };
  const [featuredGuides, setFeaturedGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [customerReviews, setCustomerReviews] = useState([]);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await fetch(`${API}/api/guides/approved`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedGuides(data);
          
          // Fetch reviews for all guides
          const allReviews = [];
          for (const guide of data) {
            try {
              const revRes = await fetch(`${API}/api/reviews/${guide.id}`);
              if (revRes.ok) {
                const revData = await revRes.json();
                if (revData.reviews && revData.reviews.length > 0) {
                  revData.reviews.forEach(r => allReviews.push({ ...r, guide_name: guide.name, guide_school: guide.school, guide_id: guide.id }));
                }
              }
            } catch {}
          }
          setCustomerReviews(allReviews);
        }
      } catch (error) {
        console.error('Failed to fetch guides:', error);
      } finally {
        setLoadingGuides(false);
      }
    };
    fetchGuides();
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://images.unsplash.com/photo-1499092346589-b9b6be3e94b2?w=1920&q=85')`
        }}
      >
        <div className="text-center text-white px-4 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-serif mb-6">Private College Campus Tours by Real Students</h1>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto">Book personalized campus tours with current college students across the USA. Experience university life through the eyes of someone who lives it every day.</p>
          <Button 
            variant="outline" 
            className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black px-8 py-6 text-base tracking-widest transition-all duration-300"
            onClick={() => navigate('/explore-schools')}
          >
            BOOK A TOUR
          </Button>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center mb-16">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {mockData.features.map((feature, index) => {
              const IconComponent = feature.icon === 'check' ? CheckSquare : feature.icon === 'dollar' ? DollarSign : Smile;
              return (
                <div key={index} className="text-center px-6">
                  <div className="flex justify-center mb-6">
                    <div className="border-4 border-gray-800 rounded-lg p-4">
                      <IconComponent className="w-12 h-12" />
                    </div>
                  </div>
                  <h3 className="text-xl font-serif mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Tour Guides Section */}
      {featuredGuides.length > 0 && (
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif mb-4">Book a Private Campus Tour</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Connect with current college students for personalized campus tours at top universities across the USA.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredGuides.map((guide) => (
                <div 
                  key={guide.id}
                  onClick={() => navigate(`/guide/${guide.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                >
                  {/* Guide Photo */}
                  <div className="relative h-64 overflow-hidden bg-gray-100">
                    {guide.photo_urls && guide.photo_urls[0] ? (
                      <img 
                        src={guide.photo_urls[0]} 
                        alt={guide.name}
                        className={`w-full h-full group-hover:scale-105 transition-transform duration-300 ${
                          guide.photo_position === 'full' ? 'object-contain' :
                          guide.photo_position === 'top' ? 'object-cover object-top' : 
                          guide.photo_position === 'bottom' ? 'object-cover object-bottom' : 
                          'object-cover object-center'
                        }`}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <User className="w-20 h-20 text-gray-400" />
                      </div>
                    )}
                    {/* Price Badge */}
                    {(guide.price_campus_tour || guide.price_video_chat) && (
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md">
                        <span className="text-sm font-bold text-gray-900">
                          From ${Math.min(guide.price_campus_tour || 999, guide.price_video_chat || 999)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Guide Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{guide.name}</h3>
                        <p className="text-gray-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" />
                          {guide.school}
                        </p>
                      </div>
                    </div>
                    
                    {/* Major/Year if available */}
                    {(guide.major || guide.academic_year) && (
                      <p className="text-sm text-gray-500 mb-3">
                        {guide.major}{guide.major && guide.academic_year && ' • '}{guide.academic_year}
                      </p>
                    )}
                    
                    {/* Bio Preview */}
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {guide.bio || guide.tour_description}
                    </p>
                    
                    {/* Tour Types Available */}
                    <div className="flex gap-2 mb-4">
                      {guide.price_campus_tour && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          <MapPin className="w-3 h-3" /> Campus Tour
                        </span>
                      )}
                      {guide.price_video_chat && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          <Video className="w-3 h-3" /> Video Tour
                        </span>
                      )}
                    </div>
                    
                    {/* View Profile Button */}
                    <Button 
                      className="w-full bg-black hover:bg-gray-800 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/guide/${guide.id}`);
                      }}
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* View All Button */}
            <div className="text-center mt-12">
              <Button 
                variant="outline"
                className="border-2 border-black hover:bg-black hover:text-white px-8 py-6 text-base tracking-widest transition-all duration-300"
                onClick={() => navigate('/explore-schools')}
              >
                EXPLORE ALL SCHOOLS
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Customer Reviews Section */}
      {customerReviews.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif mb-4">What customers are saying</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Choosing where to go to college is a big decision. Read real reviews from students and families who booked a We.Tour campus experience.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customerReviews.slice(0, 6).map((review) => {
                const guide = featuredGuides.find(g => g.id === review.guide_id);
                const guidePhoto = guide?.photo_urls?.[0] || guide?.photo_url || null;
                const isLong = review.comment && review.comment.length > 200;
                return (
                  <div 
                    key={review.id} 
                    className="relative bg-gray-50 rounded-xl p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300 cursor-pointer"
                    onClick={() => navigate(`/guide/${review.guide_id}#reviews`)}
                    data-testid={`home-review-${review.id}`}
                  >
                    {/* Decorative Quote Mark */}
                    <svg className="absolute top-6 right-6 w-10 h-10 text-blue-200 opacity-60" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983z" />
                    </svg>
                    
                    {/* Guide Info */}
                    <div className="flex items-center gap-3 mb-5">
                      {guidePhoto ? (
                        <img 
                          src={guidePhoto} 
                          alt={review.guide_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold text-lg">
                          {review.guide_name?.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-sm">{review.guide_name}</p>
                        <p className="text-xs text-gray-500">{review.guide_school}</p>
                      </div>
                    </div>
                    
                    {/* Review Text with Read More */}
                    <ReviewText comment={review.comment} isLong={isLong} />
                    
                    {/* Reviewer Name + Star Rating */}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm font-medium text-gray-700">— {review.reviewer_name || 'Anonymous'}</p>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(star => (
                          <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-blue-500 fill-blue-500' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Join Us Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif mb-8">Join us and be a part-time tour guide</h2>
          <Button 
            variant="outline" 
            className="border-2 border-black hover:bg-black hover:text-white px-8 py-6 text-base tracking-widest transition-all duration-300"
            onClick={() => navigate('/signup')}
          >
            SIGN UP
          </Button>
        </div>
      </section>

      {/* Become a Student Guide Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif mb-6">Become a Student Tour Guide</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Welcome to We.Tour! You've just found a network of passionate college students who thrive on providing visitors with truly memorable campus and local tours. 
              This could be the opportunity that takes your college experience to the next level!
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            {/* Benefit 1 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-gray-100 rounded-full p-6 mb-6">
                <Smile className="w-12 h-12 text-black" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Do What You Love</h3>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Be creative and be yourself, as you design and deliver tours that let your unique personality and knowledge shine</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Be your own boss: stay flexible and independent by setting your own schedule</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Follow your passion and do what you love while earning money as a student</span>
                </li>
              </ul>
            </div>

            {/* Benefit 2 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-gray-100 rounded-full p-6 mb-6">
                <Users className="w-12 h-12 text-black" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Meet Amazing Travelers</h3>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>The travelers who use We.Tour are passionate about exploring campuses and local areas, eager to learn from students like you</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Connect with curious visitors who value authentic, unique experiences over typical tours</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Build your network by meeting other inspiring student guides across colleges nationwide</span>
                </li>
              </ul>
            </div>

            {/* Benefit 3 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-gray-100 rounded-full p-6 mb-6">
                <DollarSign className="w-12 h-12 text-black" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Get Full Support</h3>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>We have your back! From the moment you join, you've got our team cheering you on</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Get practical training and ongoing support with every tour you lead</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>We handle all the logistics - you focus on sharing your passion for your campus and local area</span>
                </li>
              </ul>
            </div>

            {/* Benefit 4 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-gray-100 rounded-full p-6 mb-6">
                <CheckSquare className="w-12 h-12 text-black" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Grow Your Skills</h3>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Develop public speaking and communication skills valuable for any career</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Build your resume with real-world experience while still in college</span>
                </li>
                <li className="flex items-start">
                  <CheckSquare className="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-green-600" />
                  <span>Perfect for history, business, hospitality, and communications majors</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Who Can Join */}
          <div className="bg-white rounded-lg p-8 md:p-12 mb-16">
            <h3 className="text-3xl font-serif text-center mb-6">Who Can Join?</h3>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-8">
              We respect students' diverse areas of expertise and backgrounds, and are proud to welcome all majors and interests. 
              Whether you're a history buff, a foodie, an architecture enthusiast, or just love your campus and local area, we'd love to hear from you!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-3">📚</div>
                <h4 className="font-semibold mb-2">All Majors Welcome</h4>
                <p className="text-gray-600 text-sm">History, Business, Arts, Engineering - everyone has unique stories to share</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🎓</div>
                <h4 className="font-semibold mb-2">Current Students</h4>
                <p className="text-gray-600 text-sm">Current students enrolled in any college or university, as well as prospective students interested in campus tours</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">❤️</div>
                <h4 className="font-semibold mb-2">Passionate About Your Area</h4>
                <p className="text-gray-600 text-sm">Love your school and local area, and excited to share it with visitors</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Join Us CTA Section */}
      <section 
        className="relative h-96 bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1496588152823-86ff7695e68f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxuZXclMjB5b3JrJTIwc2t5bGluZXxlbnwwfHx8fDE3NjAyMTMwMTZ8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="text-center text-white px-4">
          <h2 className="text-5xl font-serif mb-8">Join Us on our next Adventure</h2>
          <Button 
            variant="outline" 
            className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black px-8 py-6 text-base tracking-widest transition-all duration-300"
            onClick={() => navigate('/signup')}
          >
            JOIN US
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;